import { useState, useEffect } from "react";
import { Text, Space, Button } from "@mantine/core";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useLocation } from "react-router-dom";

import SelectorButtonRef from "../SelectorButtonRef";
import SelectInputRef from "../SelectInputRef";
import ShadeSelection from "../shadeSelection";
import { TeethShadeType, TeethShadeSide, ItemType } from "../../types/enums";
import { STYLE, YES_OR_NO_CHOICE } from "../../types/constants";
import {
  GET_SCAN_POST_LIST,
  DELETE_MANY_ANATOMY_ITEMS,
  CREATE_ANATOMY_ITEM,
} from "../../gql/items";
import {
  formatChoicesData,
  deduceShadeType,
  checkisInlayCoreOnly,
  INLAY_CORE_SCAN_POST,
} from "../../utils/item.utils";
import {
  getAllPossibleSplintedCrown,
  isBridgeOnInlayCore,
} from "../../utils/product.utils";

const CreateNewInlayCoreComponent = ({
  productType,
  order,
  product_id,
  refetch,
  materialFormChoices,
  anatomyItemChoicesData,
  accountProductPreferences,
  onSubmit,
  isMultipleCaracteristicsByItem,
  item,
}) => {
  const location = useLocation();

  const savedItem = isMultipleCaracteristicsByItem
    ? item
    : item.find(item => item.itemType === ItemType.INLAY_CORE);
  const savedCrownItem = (location?.state?.product?.anatomyItem ?? []).find(
    item =>
      item.itemType === ItemType.CROWN ||
      item.itemType === ItemType.SPLINTED_CROWN
  );

  const product = (order?.products ?? []).find(
    product => product.id === product_id
  );

  const isNotBridgeOnInlayCore =
    !isBridgeOnInlayCore(order?.products ?? [], product) ||
    productType === ItemType.BRIDGE;

  const { data: getScanPostsData, loading: getScanPostsDataLoading } =
    useQuery(GET_SCAN_POST_LIST);

  const rhf = useForm({
    defaultValues: {
      isInlayCoreClaveted: savedItem?.inlayCoreHasClavette ?? false,
      inlayCoreMaterial: savedItem?.inlayCoreMaterial?.id,
      inlayCoreScanPost: savedItem?.inlayCoreScanPost,
      material: savedItem?.itemMaterial?.id,
      isMultiShade:
        savedCrownItem?.teethshadeType === TeethShadeType.MULTI_SHADE,
      gingivalShade:
        (savedCrownItem?.multiShadeInfo ?? []).find(
          (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
            multiShadeInfo.teethShadeSide === TeethShadeSide.GINGIVAL
        )?.itemShade?.id ?? undefined,
      baseShade:
        (savedCrownItem?.multiShadeInfo ?? []).find(
          (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
            multiShadeInfo.teethShadeSide === TeethShadeSide.BASE
        )?.itemShade?.id ?? undefined,
      incisalShade:
        (savedCrownItem?.multiShadeInfo ?? []).find(
          (multiShadeInfo: { teethShadeSide: TeethShadeSide }) =>
            multiShadeInfo.teethShadeSide === TeethShadeSide.INCISAL
        )?.itemShade?.id ?? undefined,
      shade: savedItem?.itemShade?.id,
      inlayCoreScanPostType: savedItem?.inlayCoreScanPost
        ? savedItem?.inlayCoreScanPost.split("_")[0]
        : undefined,
      isInlayCoreOnly: checkisInlayCoreOnly(product),
      isRichmondCrown: (location?.state?.product?.anatomyItem ?? []).find(
        item => item.itemType === ItemType.RICHMOND_CROWN
      )
        ? true
        : false,
      shadeType: deduceShadeType(
        savedItem?.itemShade?.id,
        order?.user?.id,
        accountProductPreferences,
        savedItem?.teethshadeType === TeethShadeType.MULTI_SHADE,
        savedItem?.multiShadeInfo ?? []
      ),
      isSplintedCrown: (location?.state?.product?.anatomyItem ?? []).find(
        item => item.itemType === ItemType.SPLINTED_CROWN
      )
        ? true
        : false,
    },
  });

  const watchInlayCoreClaveted = rhf.watch("isInlayCoreClaveted");
  const watchIsInlayCoreOnly = rhf.watch("isInlayCoreOnly");
  const watchIsRichmondCrown = rhf.watch("isRichmondCrown");
  const watchInlayCoreScanPostType = rhf.watch("inlayCoreScanPostType");
  const watchShadeType = rhf.watch("shadeType");
  const watchIsSplintedCrown = rhf.watch("isSplintedCrown");
  const watchIsMultiShade = rhf.watch("isMultiShade");
  const watchCrownMaterial = rhf.watch("material");

  const inlayCoreMaterialFormChoices = formatChoicesData(
    (anatomyItemChoicesData?.getItemMaterialsWhere ?? []).filter(
      (m: any) => m.inlayCore === true
    ),
    watchInlayCoreClaveted
  );

  if (getScanPostsDataLoading) {
    return "... Loading ...";
  }

  return (
    <>
      <Text style={{ textAlign: "center" }} fw={700}>
        {savedItem?.id ? "Modification" : "Ajout"} d'un Inlay-Core
      </Text>
      <SelectorButtonRef
        label="Inlay-Core Claveté ?"
        name="isInlayCoreClaveted"
        data={YES_OR_NO_CHOICE}
        onSubmit={selectedValue =>
          rhf.setValue("isInlayCoreClaveted", selectedValue)
        }
        control={rhf.control}
        errors={rhf.formState.errors}
        watch={watchInlayCoreClaveted}
        required={true}
      />
      <SelectorButtonRef
        label="Inlay-Core seulement (sans couronne)?"
        name="isInlayCoreOnly"
        data={YES_OR_NO_CHOICE}
        onSubmit={selectedValue =>
          rhf.setValue("isInlayCoreOnly", selectedValue)
        }
        control={rhf.control}
        errors={rhf.formState.errors}
        watch={watchIsInlayCoreOnly}
        required={true}
      />
      {!watchIsInlayCoreOnly && (
        <SelectorButtonRef
          label="Fabrication d’une couronne Richmond?"
          name="isRichmondCrown"
          data={YES_OR_NO_CHOICE}
          onSubmit={selectedValue =>
            rhf.setValue("isRichmondCrown", selectedValue)
          }
          control={rhf.control}
          errors={rhf.formState.errors}
          watch={watchIsRichmondCrown}
          required={true}
        />
      )}
      <SelectorButtonRef
        label="Choisissez votre type de Scan Post"
        name="inlayCoreScanPostType"
        data={getScanPostsData?.getScanPostsData?.types ?? []}
        onSubmit={selectedValue =>
          rhf.setValue("inlayCoreScanPostType", selectedValue)
        }
        control={rhf.control}
        errors={rhf.formState.errors}
        watch={watchInlayCoreScanPostType}
        required={true}
      />
      {watchInlayCoreScanPostType !== INLAY_CORE_SCAN_POST.ANATOMICAL.value && (
        <SelectInputRef
          label="Choisissez le scan post utilisé"
          name="inlayCoreScanPost"
          data={
            watchInlayCoreScanPostType
              ? (
                  getScanPostsData?.getScanPostsData.scanPosts as {
                    type: string;
                    value: string;
                    label: string;
                  }[]
                ).filter(
                  scanPost => scanPost.type === watchInlayCoreScanPostType
                )
              : getScanPostsData?.getScanPostsData.scanPosts
          }
          control={rhf.control}
          errors={rhf.formState.errors}
          required={
            watchInlayCoreScanPostType !== INLAY_CORE_SCAN_POST.ANATOMICAL.value
          }
        />
      )}
      <SelectInputRef
        label="Matériau d'Inlay-Core"
        name="inlayCoreMaterial"
        data={inlayCoreMaterialFormChoices}
        control={rhf.control}
        errors={rhf.formState.errors}
        required={true}
      />
      {(productType === ItemType.INLAY_CORE ||
        productType === ItemType.CROWN) &&
        getAllPossibleSplintedCrown(product?.teeth ?? []).length > 0 &&
        !watchIsInlayCoreOnly &&
        !watchIsRichmondCrown &&
        isNotBridgeOnInlayCore && (
          <SelectorButtonRef
            label="Faut-il solidariser les couronnes ?"
            name="isSplintedCrown"
            data={YES_OR_NO_CHOICE}
            onSubmit={selectedValue =>
              rhf.setValue("isSplintedCrown", selectedValue)
            }
            control={rhf.control}
            errors={rhf.formState.errors}
            watch={watchIsSplintedCrown}
            required={true}
          />
        )}
      {!watchIsInlayCoreOnly && (
        <>
          <SelectInputRef
            label="Matériau de la couronne"
            name="material"
            data={materialFormChoices.filter(material => material?.crown)}
            control={rhf.control}
            errors={rhf.formState.errors}
            required={true}
          />
          {watchCrownMaterial !== "METAL_NON_PRECIOUS" && (
            <ShadeSelection
              watchShadeType={watchShadeType}
              control={rhf.control}
              errors={rhf.formState.errors}
              anatomyItemChoicesData={anatomyItemChoicesData}
              onSelect={selectedValue =>
                rhf.setValue("shadeType", selectedValue)
              }
              order={order}
              accountProductPreferences={accountProductPreferences}
              rhf={rhf}
              itemType={ItemType.INLAY_CORE}
              user={order?.user}
            />
          )}
        </>
      )}
      <div style={{ textAlign: "center" }}>
        <Button
          onClick={rhf.handleSubmit(data =>
            onSubmit(
              data,
              watchIsInlayCoreOnly,
              watchIsMultiShade,
              watchInlayCoreScanPostType,
              isNotBridgeOnInlayCore,
              savedItem
            )
          )}
          style={{ backgroundColor: STYLE.primary }}
        >
          Submit
        </Button>
      </div>
    </>
  );
};

export default CreateNewInlayCoreComponent;
